// panels/TimeSeriesPanel.jsx
import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Legend,
} from 'recharts';
import { PanelWrapper } from './PanelWrapper';

const SERIES = [
  { key: 'total',  color: '#3b82f6', label: 'Total',  gradient: 'gTotal'  },
  { key: 'errors', color: '#ef4444', label: 'Errors', gradient: 'gErrors' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 11,
    }}>
      <div style={{ color: 'var(--text-3)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, fontFamily: "'Space Mono', monospace" }}>
          {p.name}: {p.value?.toLocaleString()}
        </div>
      ))}
      {payload.length >= 2 && payload[0].value > 0 && (
        <div style={{ color: 'var(--text-3)', marginTop: 4 }}>
          Error rate: {((payload[1].value / payload[0].value) * 100).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

export function TimeSeriesPanel({ data = [], annotations = [], showAnnotations = true, loading, title = 'Log Volume Trend', subtitle }) {
  return (
    <PanelWrapper title={title} subtitle={subtitle} loading={loading} datasource="MongoDB">
      <div style={{ padding: '12px 4px 8px 0', height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <defs>
              {SERIES.map(s => (
                <linearGradient key={s.gradient} id={s.gradient} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={s.color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid stroke="rgba(255,255,255,.04)" strokeDasharray="3 3" />
            <XAxis
              dataKey="time" tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={false} tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={false} tickLine={false} width={42}
              tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              formatter={(val) => <span style={{ color: 'var(--text-2)' }}>{val}</span>}
            />

            {SERIES.map(s => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                fill={`url(#${s.gradient})`}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, stroke: s.color, fill: s.color }}
              />
            ))}

            {/* Annotations as vertical reference lines */}
            {showAnnotations && annotations.map((a, i) => {
              const matchedBucket = data.findIndex(d => d.rawTime && Math.abs(new Date(d.rawTime) - new Date(a.time)) < 300000);
              const xVal = matchedBucket >= 0 ? data[matchedBucket]?.time : null;
              return xVal ? (
                <ReferenceLine
                  key={i}
                  x={xVal}
                  stroke={a.color || '#f59e0b'}
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  label={{ value: a.title, position: 'top', fontSize: 9, fill: a.color || '#f59e0b' }}
                />
              ) : null;
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </PanelWrapper>
  );
}
